import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

const getGeneratedLabel = (generatedAt) => {
  if (generatedAt instanceof Date && !Number.isNaN(generatedAt.getTime())) {
    return generatedAt.toLocaleString();
  }
  if (typeof generatedAt === 'string') return generatedAt;
  return new Date().toLocaleString();
};

const loadImage = async (src) => {
  if (!src) return null;
  try {
    const response = await fetch(src, { cache: 'force-cache' });
    if (!response.ok) throw new Error(`Failed to load image: ${src}`);
    return await response.blob();
  } catch (error) {
    console.warn('[PDF] Failed to load image', src, error);
    return null;
  }
};

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const getInitials = (name = '') => {
  const parts = name.trim().split(/\\s+/).filter(Boolean);
  if (parts.length === 0) return 'U';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

export default async function exportAnalyticsPdfWithTemplate({
  rootNode,
  title = 'Learning Journey',
  selectedUser,
  generatedAt = new Date(),
  fileName = `learning-analytics-${new Date().toISOString().slice(0, 10)}.pdf`,
}) {
  if (!rootNode) {
    throw new Error('rootNode is required to capture the analytics layout.');
  }

  const canvas = await html2canvas(rootNode, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    windowWidth: rootNode.scrollWidth,
    windowHeight: rootNode.scrollHeight,
    logging: false,
  });

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 40;
  const headerHeight = 90;

  const logoBlob = await loadImage('/logo.png');
  const avatarSrc =
    selectedUser?.profilePictureUrl ||
    selectedUser?.avatarUrl ||
    selectedUser?.avatar ||
    selectedUser?.photoUrl ||
    null;
  const avatarBlob = await loadImage(avatarSrc);

  const logoSize = 48;
  if (logoBlob) {
    try {
      const logoDataUrl = await blobToDataUrl(logoBlob);
      pdf.addImage(logoDataUrl, 'PNG', margin, margin, logoSize, logoSize);
    } catch (err) {
      console.warn('[PDF] Failed to embed logo', err);
    }
  }

  const userName = selectedUser?.name || selectedUser?.label || 'Selected User';
  const userEmail = selectedUser?.email || '';
  const generatedLabel = getGeneratedLabel(generatedAt);

  const textX = margin + logoSize + 16;
  pdf.setTextColor(22, 35, 64);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(24);
  pdf.text(title, textX, margin + 26);

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(14);
  pdf.text(userName, textX, margin + 46);

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(11);
  if (userEmail) {
    pdf.text(userEmail, textX, margin + 60);
  }
  pdf.setFontSize(10);
  pdf.text(`Generated: ${generatedLabel}`, textX, margin + 74);

  const avatarSize = 54;
  const avatarX = pageWidth - margin - avatarSize;
  const avatarY = margin;
  if (avatarBlob) {
    try {
      const avatarDataUrl = await blobToDataUrl(avatarBlob);
      pdf.addImage(avatarDataUrl, 'PNG', avatarX, avatarY, avatarSize, avatarSize, undefined, 'FAST');
    } catch (err) {
      console.warn('[PDF] Failed to embed avatar', err);
      pdf.setDrawColor(180);
      pdf.circle(avatarX + avatarSize / 2, avatarY + avatarSize / 2, avatarSize / 2, 'S');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(16);
      pdf.text(getInitials(userName), avatarX + avatarSize / 2 - 9, avatarY + avatarSize / 2 + 5);
    }
  } else {
    pdf.setDrawColor(180);
    pdf.circle(avatarX + avatarSize / 2, avatarY + avatarSize / 2, avatarSize / 2, 'S');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(16);
    pdf.text(getInitials(userName), avatarX + avatarSize / 2 - 9, avatarY + avatarSize / 2 + 5);
  }

  pdf.setDrawColor(210);
  pdf.setLineWidth(0.6);
  pdf.line(margin, margin + headerHeight - 6, pageWidth - margin, margin + headerHeight - 6);

  const availableWidth = pageWidth - margin * 2;
  const availableHeight = pageHeight - (margin + headerHeight + 6) - margin;

  const scale = Math.min(
    availableWidth / canvas.width,
    availableHeight / canvas.height,
  );

  const imageWidth = canvas.width * scale;
  const imageHeight = canvas.height * scale;

  const offsetX = margin + (availableWidth - imageWidth) / 2;
  const offsetY = margin + headerHeight + 8;

  const imgData = canvas.toDataURL('image/png');
  pdf.addImage(imgData, 'PNG', offsetX, offsetY, imageWidth, imageHeight, undefined, 'FAST');

  pdf.save(fileName);
}





